import React, { FC, useContext, useEffect } from 'react';
import assetLibrary from 'app/utils/assetLibrary';
import {
  selectCustomer,
  selectCustomerProduct,
  selectCustomerReferralSources,
  selectLastProductIntake,
} from 'app/selectors/customer';
import './css/HomePage.scss';
import { useAppSelector } from 'app/helpers/hooks';
import ProtocolCard from './ProtocolCard';
import HomePageResources from './HomePageResources';
import HomePageSocialMedia from './HomePageSocialMedia';
import { PROTOCOLS, Protocol } from './helpers/protocols';
import homePageNextItems from 'app/components/customer/dashboard/HomePage/helpers/homePageNextItems';
import ProtocolUpsellCard from './ProtocolUpsellCard';
import homePageShipmentNotifications from 'app/components/customer/dashboard/HomePage/helpers/homePageShipmentNotifications';
import ReferralCard from 'app/components/customer/Referral/ReferralCard';
import ReferralSourcesModal from 'app/components/customer/dashboard/ReferralSourcesModal';
import { ProductContext } from 'app/components/customer/Product';

const UserHeader: FC = () => {
  const customer = useAppSelector(selectCustomer);
  const name = customer.get('first_name');

  return (
    <div className="dash-card user-header mt-2">
      <div className="flex flex-row gap-2">
        <img className="me-4 user-header__avatar" src={assetLibrary['user-placeholder']} alt="user avatar" />
        <div>
          <p className="user-header__greeting">Welcome Back</p>
          <p className="user-header__name">{name}</p>
        </div>
      </div>
    </div>
  );
};

const DelayBanner: FC<{ activeProtocols: Protocol[] }> = ({ activeProtocols }) => {
  if (activeProtocols.some((protocol) => protocol.slug === 'magician')) {
    const product = useAppSelector((state) => selectCustomerProduct(state, 'magician'));
    if (product.get('cancelled_at')) return null;

    return (
      <div className="dash-card delay-banner">
        <span>
          Due to high demand,{' '}
          <b>shipping times have been updated to 7 to 10 business days for Weight Loss protocols.</b>
          Your refill process will start earlier in order to accommodate these changes. Thank you for your patience!
        </span>
      </div>
    );
  }

  return null;
};

const HomePage = () => {
  const productCtx = useContext(ProductContext);
  const customer = useAppSelector(selectCustomer);
  const activeProtocols = PROTOCOLS.filter((item) =>
    customer.getIn(['maximus_products', item.slug])?.get('current_subscription'),
  );
  const currentIntake = useAppSelector((state) => selectLastProductIntake(state, productCtx.selectedProduct));
  const clinicianQuestionsCompletedAt = currentIntake?.get('clinician_questions_completed_at');
  const referralSources = useAppSelector(selectCustomerReferralSources);
  const showReferralSourcesModal = !!clinicianQuestionsCompletedAt && !referralSources;
  const ongoingProtocols = PROTOCOLS.filter(
    (item) =>
      !activeProtocols.includes(item) &&
      customer
        .getIn(['maximus_products', item.slug])
        ?.get('intakes')
        ?.last()
        ?.get('steps')
        ?.some((step) => step.get('completed')),
  );
  const upsellProtocols = PROTOCOLS.filter(
    (item) => !activeProtocols.includes(item) && !ongoingProtocols.includes(item),
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      {showReferralSourcesModal && <ReferralSourcesModal />}
      <div className="dash-bg" />
      <div className="dash centered flex-column flex-between px-5 gap-8 mb-10">
        <DelayBanner activeProtocols={activeProtocols} />
        <UserHeader />
        {activeProtocols.concat(ongoingProtocols).map((protocol) => {
          const items = homePageNextItems(customer, protocol.slug).map(({ title, description, cta }) => ({
            title,
            description,
            cta,
            url: `?product=${protocol.slug}`,
          }));

          const notifications = homePageShipmentNotifications(customer)
            .filter(({ protocolName: product }) => product === protocol.slug)
            .map(({ title, description, trackingUrl }) => ({
              title,
              description,
              trackingUrl,
            }));

          return (
            <ProtocolCard
              protocol={protocol}
              items={items}
              notifications={notifications}
              key={`protocol-card-${protocol.slug}`}
            />
          );
        })}
        {upsellProtocols.map((protocol) => (
          <ProtocolUpsellCard protocol={protocol} key={`protocol-card-${protocol.slug}`} />
        ))}
        <ReferralCard />
      </div>
      <HomePageResources />
      <HomePageSocialMedia />
    </>
  );
};

export default HomePage;
